import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { Field, Input, PasswordInput } from '../components/ui/Field';
import { Logo } from '../components/ui/Logo';

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  if (!auth.isLoading && auth.isAuthenticated) return <Navigate to="/dashboard" replace />;

  const submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setError('');
    const values = new FormData(event.currentTarget);
    try {
      await auth.login.mutateAsync({ email: String(values.get('email')), password: String(values.get('password')) });
      const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Could not sign in.'); }
  };
  const submitRecovery = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setError(''); setSuccess('');
    const values = Object.fromEntries(new FormData(event.currentTarget));
    if (values.new_password !== values.confirm_password) { setError('The new passwords do not match.'); return; }
    try {
      await api.post('/auth/forgot-password', values);
      setSuccess('Password reset. You can sign in with the new password.');
      setRecovery(false);
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Could not reset the password.'); }
  };

  return <main className="auth-page">
    <div className="auth-page__pattern" aria-hidden="true" />
    <section className="auth-card page-enter">
      <Logo />
      <div className="auth-card__heading"><h1>{recovery ? 'Reset your password' : 'Welcome back'}</h1><p>{recovery ? 'Use the original credentials stored securely on the server.' : 'Sign in to manage your matrimony profiles.'}</p></div>
      {error ? <div className="alert alert--error" role="alert">{error}</div> : null}
      {success ? <div className="alert alert--success" role="status">{success}</div> : null}
      {recovery ? <form onSubmit={submitRecovery} className="form-stack">
        <Field label="Original email" htmlFor="recovery_email"><Input id="recovery_email" name="recovery_email" type="email" autoComplete="username" required /></Field>
        <Field label="Original password" htmlFor="recovery_password"><PasswordInput id="recovery_password" name="recovery_password" autoComplete="current-password" required /></Field>
        <Field label="New password" htmlFor="new_password"><PasswordInput id="new_password" name="new_password" minLength={8} required /></Field>
        <Field label="Confirm new password" htmlFor="confirm_password"><PasswordInput id="confirm_password" name="confirm_password" minLength={8} required /></Field>
        <Button type="submit">Reset password</Button>
        <button type="button" className="text-button" onClick={() => { setRecovery(false); setError(''); }}>Back to sign in</button>
      </form> : <form onSubmit={submitLogin} className="form-stack">
        <Field label="Email address" htmlFor="email"><Input id="email" name="email" type="email" autoComplete="username" required /></Field>
        <Field label="Password" htmlFor="password"><PasswordInput id="password" name="password" autoComplete="current-password" required /></Field>
        <Button type="submit" loading={auth.login.isPending}>Sign in</Button>
        <button type="button" className="text-button" onClick={() => { setRecovery(true); setError(''); }}>Forgot password?</button>
      </form>}
      <small className="auth-card__footer">Private administrator access · AccheyRishtey</small>
    </section>
  </main>;
}
